import Dexie, { type Table } from 'dexie';
import type { OutboxOp, SyncTable } from '@pos/sync';
import type {
  AccountEntryRow,
  BusinessRow,
  CashMovementRow,
  CashSessionRow,
  CustomerBalanceRow,
  CustomerRow,
  CategoryRow,
  CursorRow,
  DeviceRow,
  LocalCredential,
  MembershipRow,
  MetaEntry,
  ProductRow,
  PurchaseItemRow,
  PurchaseRow,
  SaleRow,
  StockLevelRow,
  StockMovementRow,
  SupplierRow,
  SyncRejectionRow,
  RegisterRow,
  VariantCostRow,
  VariantRow,
} from './types.ts';

/**
 * Tablas con datos que solo ve el dueño (o un empleado con permiso). En un dispositivo compartido se borran
 * al cerrar la sesión del dueño (y se vuelven a bajar cuando entra de nuevo), para que
 * un empleado no pueda leerlas desde las herramientas del navegador.
 */
export const OWNER_ONLY_TABLES = ['variant_costs', 'purchases', 'purchase_items', 'customer_balances', 'sync_rejections'] as const satisfies readonly SyncTable[];

/**
 * Base local del dispositivo (IndexedDB). Las tablas sincronizadas replican las del
 * servidor; outbox es la cola de operaciones pendientes de enviar.
 *
 * Cambios de esquema: agregar un nuevo this.version(n) — nunca modificar uno existente.
 */
export class LocalDb extends Dexie {
  businesses!: Table<BusinessRow, string>;
  memberships!: Table<MembershipRow, string>;
  devices!: Table<DeviceRow, string>;
  categories!: Table<CategoryRow, string>;
  suppliers!: Table<SupplierRow, string>;
  products!: Table<ProductRow, string>;
  product_variants!: Table<VariantRow, string>;
  variant_costs!: Table<VariantCostRow, string>;
  stock_levels!: Table<StockLevelRow, string>;
  stock_movements!: Table<StockMovementRow, string>;
  purchases!: Table<PurchaseRow, string>;
  purchase_items!: Table<PurchaseItemRow, string>;
  sales!: Table<SaleRow, string>;
  customers!: Table<CustomerRow, string>;
  cash_sessions!: Table<CashSessionRow, string>;
  cash_movements!: Table<CashMovementRow, string>;
  account_entries!: Table<AccountEntryRow, string>;
  customer_balances!: Table<CustomerBalanceRow, string>;
  sync_rejections!: Table<SyncRejectionRow, string>;
  registers!: Table<RegisterRow, string>;
  outbox!: Table<OutboxOp, number>;
  cursors!: Table<CursorRow, string>;
  credentials!: Table<LocalCredential, string>;
  meta!: Table<MetaEntry, string>;

  constructor(name = 'pos-saas') {
    super(name);
    this.version(1).stores({
      businesses: 'id',
      memberships: 'id, business_id, user_id, [business_id+username]',
      devices: 'id, business_id',
      outbox: '++seq, status, [status+created_by+seq], [table+row_id]',
      cursors: 'key',
      credentials: 'id, business_id, user_id',
      meta: 'key',
    });
    // Etapa 2: catálogo.
    this.version(2).stores({
      categories: 'id, business_id',
      suppliers: 'id, business_id',
      products: 'id, business_id',
      product_variants: 'id, business_id, product_id, barcode, sku',
      variant_costs: 'id, business_id',
      outbox: '++seq, status, [status+created_by+seq], [table+row_id], [table+status]',
    });
    // Etapa 3: stock y compras.
    this.version(3).stores({
      stock_levels: 'id, business_id, variant_id',
      stock_movements: 'id, business_id, variant_id, created_at',
      purchases: 'id, business_id, occurred_at',
      purchase_items: 'id, business_id, purchase_id',
    });
    // Etapa 4: ventas de este dispositivo.
    this.version(4).stores({
      sales: 'id, business_id, occurred_at, number, original_sale_id',
    });
    // Etapa 5: caja, clientes y cuenta corriente.
    this.version(5).stores({
      sales: 'id, business_id, occurred_at, number, original_sale_id, cash_session_id, customer_id',
      customers: 'id, business_id',
      cash_sessions: 'id, business_id, device_id, opened_at',
      cash_movements: 'id, business_id, session_id, created_at',
      account_entries: 'id, business_id, customer_id, cash_session_id, created_at',
      customer_balances: 'id, business_id, customer_id',
    });
    // Etapa 7: cuarentena de cambios rechazados (y buscar operaciones por op_id).
    this.version(6).stores({
      sync_rejections: 'id, business_id, status',
      outbox: '++seq, status, op_id, [status+created_by+seq], [table+row_id], [table+status]',
    });
    // Cajas compartidas: las cajas del negocio y buscar turnos por caja.
    this.version(7).stores({
      registers: 'id, business_id',
      cash_sessions: 'id, business_id, device_id, opened_at, register_id',
    });
  }

  syncTable(table: SyncTable): Table<{ id: string }, string> {
    return this.table(table);
  }
}
